import { Controller } from "@hotwired/stimulus";

/**
 * Pricing Controller - Price display & recurrence management
 *
 * Responsibilities:
 * • Monthly/yearly recurrence switching & display
 * • Viewer dropdown pricing updates (premium plans)
 * • Price display from backend data
 * • Currency formatting integration with CurrencyManage.js
 * • Inter-controller communication via plan-changed events
 *
 * Replaces jQuery:
 * • CurrencyManage.updateCurrencyElements()
 * • Plan.updateViewerCount() pricing logic
 * • Monthly/yearly toggle functionality
 */

export default class extends Controller {
  static REC_MONTH = "month";
  static REC_YEAR = "year";

  static DEFAULTS = {
    DEFAULT_RECURRENCE: "year"
  };

  static targets = [
    "monthlyPrice",
    "yearlyPrice",
    "monthlyAmount",
    "yearlyAmount",
    "savingsAmount",
    "billingText",
    "billingAmount",
    "viewerSelect",
    "planCard", // Generic target for all plan cards by category
  ];

  static values = {
    selectedPlan: String,
    recurrence: String,
    currentRecurrence: String,
    currentPlan: String
  };

  connect() {
    this._requestId = 0;
    this.recurrenceValue ||= this.currentRecurrenceValue || this.constructor.DEFAULTS.DEFAULT_RECURRENCE;

    this.toggleRecurrenceDisplay();
    this.initializeViewerCount();
    this.bindEvents();
    this.updateCurrencyDisplay();
  }

  disconnect() {
    this.unbindEvents();
  }

  bindEvents() {
    this.element.addEventListener("plans:plan-changed", this.handlePlanChanged);
    this.element.addEventListener("plans:recurrence-changed", this.handleRecurrence);

    // We need to listen to currency dropdown changes globally since the dropdown lives
    // outside this controller's scope. We may refactor the currency selector to a stimulus controller in future
    $(document).on("click", ".currency-dropdown a[data-curr]", this.handleCurrencyChange);
  }

  unbindEvents() {
    this.element.removeEventListener("plans:plan-changed", this.handlePlanChanged);
    this.element.removeEventListener("plans:recurrence-changed", this.handleRecurrence);
    $(document).off("click", ".currency-dropdown a[data-curr]", this.handleCurrencyChange);
  }

  // Events
  nextRequest() {
    return ++this._requestId;
  }

  isLatest(id) {
    return id === this._requestId;
  }

  handlePlanChanged = (event) => this.onPlanChanged(event.detail.planName);

  handleRecurrence = (event) => this.onRecurrenceChanged(event.detail.recurrence);

  handleCurrencyChange = () => {
    setTimeout(() => this.updateCurrencyDisplay(), 50);
  };

  // Inter-controller communication

  onPlanChanged(planName) {
    this.selectedPlanValue = this.normalizePlanName(planName);
  }

  onRecurrenceChanged(recurrence) {
    this.recurrenceValue = recurrence;
    this.recurrenceValueChanged();
  }

  normalizePlanName(planName) {
    return this.viewerSelectTarget?.selectedOptions?.[0]?.dataset?.plan || planName;
  }

  handleViewerSelection() {
    const selectedOption = this.viewerSelectTarget?.selectedOptions?.[0];
    if (!selectedOption) return;

    const { plan } = selectedOption.dataset;
    if (!plan) return;

    this.selectedPlanValue = plan;

    // Find the card to get its category
    const card = this.planCardTargets.find(c => c.dataset.plan === plan);
    const category = card?.dataset.category;

    // Notify plans controller
    this.dispatch("plan-changed", { detail: { planName: plan, category: category }, bubbles: true });

    const requestId = this.nextRequest();
    this.fetchBackendPricing(plan, this.recurrenceValue, requestId);
  }

  initializeViewerCount() {
    if (!this.hasViewerSelectTarget || !this.currentPlanValue) return;
    const option = this.viewerSelectTarget.querySelector(`option[data-plan="${this.currentPlanValue}"]`);
    if (option) this.viewerSelectTarget.value = option.value;
  }

  // Display updates

  updateField(targets, card, euroValue) {
    const el = targets.find(t => card.contains(t));
    if (!el) return;

    // Update data-euro attribute with EUR value.
    // CurrencyManage.updateCurrencyElements() will read this and convert/display in selected currency.
    el.dataset.euro = euroValue;

    const inner = el.querySelector("span.amount[data-euro]");
    if (inner) {
      inner.dataset.euro = euroValue;
    }
  }

  toggleRecurrenceDisplay() {
    const isMonthly = this.recurrenceValue === this.constructor.REC_MONTH;

    this.monthlyPriceTargets.forEach(price => price.classList.toggle("hidden", !isMonthly));
    this.yearlyPriceTargets.forEach(price => price.classList.toggle("hidden", isMonthly));

    this.billingTextTargets.forEach(text => {
      text.classList.toggle("billed-per-month", isMonthly);
      text.classList.toggle("billed-per-year", !isMonthly);
    });
  }

  async refreshAllPlanPrices(requestId = null) {
    // Fetch pricing for all plan cards. If adding dropdowns to multiple categories,
    // associate each dropdown with its card and deduplicate requests by (planName, recurrence).
    const fetchPromises = [];

    this.planCardTargets.forEach(card => {
      const capabilities = JSON.parse(card.dataset.capabilities || "{}");

      // For plans with viewer dropdown, use the selected option from dropdown
      // Otherwise use the plan name from the card
      let planName;
      if (capabilities.hasViewerDropdown && this.hasViewerSelectTarget) {
        planName = this.viewerSelectTarget.selectedOptions?.[0]?.dataset?.plan ||
                   this.viewerSelectTarget.options[0]?.dataset?.plan;
      } else {
        planName = card.dataset.plan;
      }

      if (planName) {
        fetchPromises.push(this.fetchBackendPricing(planName, this.recurrenceValue, requestId));
      }
    });

    await Promise.all(fetchPromises);
  }

  async fetchBackendPricing(planName, recurrence, requestId = null) {
    try {
      const response = await fetch("/admin/plan_previews", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        },
        body: JSON.stringify({
          plan: {
            plan_type_name: planName,
            recurrence: recurrence
          }
        })
      });

      if (!response.ok) return;

      const json = await response.json();

      // Prevent race conditions from rapid toggling
      if (requestId && !this.isLatest(requestId)) return;

      this.applyBackendPricing(json);
    } catch (e) {
      console.error("Error fetching backend plan preview:", e);
    }
  }

  applyBackendPricing(data) {
    if (!data?.amounts || data.amounts.per_month == null) return;

    const { per_month, billed_per_period, savings = 0 } = data.amounts;
    const { category, recurrence } = data;

    this.updateDisplayedPrices(category, recurrence, per_month, billed_per_period, savings);
  }

  findCardForPlan(category) {
    return this.planCardTargets.find(card => card.dataset.category === category);
  }

  updateDisplayedPrices(category, recurrence, perMonth, billedPerPeriod, savings) {
    const card = this.findCardForPlan(category);
    if (!card) return;

    if (recurrence === this.constructor.REC_MONTH) {
      this.updateField(this.monthlyAmountTargets, card, perMonth);
    } else {
      this.updateField(this.yearlyAmountTargets, card, perMonth);
      this.updateField(this.savingsAmountTargets, card, savings);
      this.updateField(this.billingAmountTargets, card, billedPerPeriod);
    }

    this.updateCurrencyDisplay();
  }

  updateCurrencyDisplay() {
    // Trigger currency conversion using existing CurrencyManage.js system
    if (window.CurrencyManage) CurrencyManage.updateCurrencyElements();
  }

  async recurrenceValueChanged() {
    const requestId = this.nextRequest();
    await this.refreshAllPlanPrices(requestId);

    // Prevent UI updates from stale requests if user toggles rapidly
    if (!this.isLatest(requestId)) return;
    this.toggleRecurrenceDisplay();
  }
}
